/**
 * Exam simulation selection page.
 *
 * Main page for exam simulation selection flow. Guides users through
 * three steps: exam type selection → level selection → module selection.
 *
 * @module ExamSimulation
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ExamTypeSelector } from "@/components/exam/ExamTypeSelector";
import { LevelSelector } from "@/components/exam/LevelSelector";
import { ModuleSelector } from "@/components/exam/ModuleSelector";
import { InstructionsScreen } from "@/components/exam/InstructionsScreen";
import { PreparingScreen } from "@/components/exam/PreparingScreen";
import { ModuleIntroduction } from "@/components/exam/ModuleIntroduction";
import { ReadingInterface } from "@/components/exam/ReadingInterface";
import { ListeningInterface } from "@/components/exam/ListeningInterface";
import { SpeakingInterface } from "@/components/exam/SpeakingInterface";
import { WritingInterface } from "@/components/exam/writing/WritingInterface";
import { SpeakingModuleIntroduction } from "@/components/exam/SpeakingModuleIntroduction";
import { useExamSimulationStore } from "@/stores/useExamSimulationStore";
import { useMicrophonePermission } from "@/hooks/useMicrophonePermission";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  findSimulationSettings,
  fetchModuleIntroduction,
  startSimulationAttempt,
} from "@/lib/examApi";
import { getCurrentPlan } from "@/lib/planApi";
import { currentPlanQueryKey, useCurrentPlan } from "@/hooks/useCurrentPlan";
import {
  canStartSimulation,
  SIMULATION_RENEWAL_BLOCK_MESSAGE,
} from "@/lib/planAccess";
import { diagnosticProfileQueryKey } from "@/hooks/useDiagnosticProfile";
import { getDiagnosticProfile } from "@/lib/diagnosticApi";
import { toast } from "@/components/ui/sonner";
import { STALE_TIME_FIVE_MINUTES } from "@/lib/constants";
import { logger } from "@/lib/logger";
import type {
  CEFRLevel,
  ExamModule,
  ExamType,
  ModuleIntroduction as ModuleIntroductionType,
} from "@/types/exam-simulation";
import { ROUTES } from "@/lib/routeConstants";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StandardPageShell } from "@/components/layout/StandardPageShell";
import { ResumeSimulationDialog } from "@/components/exam/ResumeSimulationDialog";
import { SimulationDraftSaveIndicator } from "@/components/exam/SimulationDraftSaveIndicator";
import {
  clearAllForUser,
  getNewestRecoverableDraft,
  type DraftIndexEntry,
} from "@/lib/simulationDraftStorage";
import type { ExamSimulationLocationState } from "@/lib/examSimulationNavState";
import { useSimulationDraftPersistStore } from "@/stores/useSimulationDraftPersistStore";

const VALID_EXAM_TYPES = new Set<ExamType>(["goethe", "osd", "telc"]);
const VALID_LEVELS = new Set<CEFRLevel>(["A1", "A2", "B1", "B2", "C1", "C2"]);

export default function ExamSimulation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.userType === "admin";
  const { plan, isLoading: isPlanLoading } = useCurrentPlan(!isAdmin);

  // Zustand store for client state
  const {
    selection,
    currentStep,
    sessionId,
    attemptToken,
    setExamType,
    setLevel,
    toggleModule,
    setModules,
    goToInstructions,
    goToPreparing,
    goToModuleIntroduction,
    goToModule,
    goToFinished,
    goBack,
    reset,
    generateSimulationSessionId,
    setAttemptToken,
    rehydrateFromDraft,
  } = useExamSimulationStore();

  const resetDraftPersistUi = useSimulationDraftPersistStore((s) => s.reset);

  useEffect(() => {
    if (currentStep !== "module") {
      resetDraftPersistUi();
    }
  }, [currentStep, resetDraftPersistUi]);

  // React Query for server state (settings)
  const {
    data: settings,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["simulationSettings", selection.level, selection.examType],
    queryFn: () => {
      if (!selection.level || !selection.examType) {
        return Promise.resolve(null);
      }
      return findSimulationSettings(selection.level, selection.examType);
    },
    enabled: !!selection.level && !!selection.examType,
    staleTime: STALE_TIME_FIVE_MINUTES,
  });

  const {
    permission: microphonePermission,
    requestPermission: requestMicrophonePermission,
    isSupported: isMicrophoneSupported,
  } = useMicrophonePermission();

  // Track current module being simulated
  const [currentModule, setCurrentModule] = useState<ExamModule | null>(null);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);

  // Draft recovery flow
  const [recoverableDraft, setRecoverableDraft] =
    useState<DraftIndexEntry | null>(null);
  const [recoveryHandled, setRecoveryHandled] = useState(false);

  // Fetch module introduction when current module is set
  const { data: moduleIntroduction, isLoading: isLoadingIntroduction } =
    useQuery<ModuleIntroductionType | null>({
      queryKey: [
        "moduleIntroduction",
        currentModule,
        selection.examType,
        selection.level,
      ],
      queryFn: () => {
        if (!currentModule || !selection.examType || !selection.level) {
          return Promise.resolve(null);
        }
        return fetchModuleIntroduction(
          currentModule,
          selection.examType,
          selection.level,
        );
      },
      enabled: !!currentModule && !!selection.examType && !!selection.level,
      staleTime: STALE_TIME_FIVE_MINUTES,
    });

  // Initialize modules from settings when they're loaded
  useEffect(() => {
    if (settings && selection.modules.length === 0) {
      if (settings.moduleOrder.length > 0) {
        setModules(settings.moduleOrder);
      } else {
        // Default: all modules selected
        setModules(["reading", "listening", "writing", "speaking"]);
      }
    }
  }, [settings, selection.modules.length, setModules]);

  const hasHydratedFromSearchParams = useRef(false);
  const renewalRedirectHandled = useRef(false);
  const activeAttemptSteps = new Set([
    "preparing",
    "module-intro",
    "module",
    "finished",
  ]);
  const hasActiveAttempt =
    Boolean(attemptToken) && activeAttemptSteps.has(currentStep);

  useEffect(() => {
    if (
      renewalRedirectHandled.current ||
      isAdmin ||
      isPlanLoading ||
      hasActiveAttempt
    ) {
      return;
    }
    if (!canStartSimulation(plan, isAdmin)) {
      renewalRedirectHandled.current = true;
      toast.error(
        t("simulation.renewRequired", {
          defaultValue: SIMULATION_RENEWAL_BLOCK_MESSAGE,
        }),
        {
          action: {
            label: t("simulation.viewPlans", { defaultValue: "View plans" }),
            onClick: () => navigate(`${ROUTES.SETTINGS}#plan`),
          },
        },
      );
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
  }, [
    hasActiveAttempt,
    isAdmin,
    isPlanLoading,
    navigate,
    plan,
    t,
  ]);

  // Deep-link quick start from Dashboard, e.g. /exam?examType=goethe&level=A1
  useEffect(() => {
    if (hasHydratedFromSearchParams.current) {
      return;
    }

    const examTypeParam = searchParams.get("examType");
    const levelParam = searchParams.get("level");

    if (
      examTypeParam &&
      levelParam &&
      VALID_EXAM_TYPES.has(examTypeParam as ExamType) &&
      VALID_LEVELS.has(levelParam as CEFRLevel)
    ) {
      setExamType(examTypeParam as ExamType);
      setLevel(levelParam as CEFRLevel);
      hasHydratedFromSearchParams.current = true;
    }
  }, [searchParams, setExamType, setLevel]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(t("simulation.errors.unableToLoad"));
    }
  }, [error, t]);

  const applyResumeFromDraft = useCallback(
    (draft: DraftIndexEntry) => {
      if (!draft.examType || !draft.level || draft.modules.length === 0) {
        return;
      }
      rehydrateFromDraft({
        selection: {
          examType: draft.examType,
          level: draft.level,
          modules: [...draft.modules],
        },
        currentStep: draft.step,
        sessionId: draft.sessionId,
      });
      setCurrentModule(draft.module);
      setCurrentModuleIndex(Math.max(0, draft.moduleIndex));
      logger.info("Resumed simulation from draft", {
        sessionId: draft.sessionId,
        module: draft.module,
        step: draft.step,
      });
    },
    [rehydrateFromDraft],
  );

  const authorizeAndResumeDraft = useCallback(
    async (draft: DraftIndexEntry) => {
      if (!draft.examType || !draft.level || draft.modules.length === 0) {
        return false;
      }
      const result = await startSimulationAttempt(
        draft.level,
        draft.examType,
        draft.modules,
        draft.sessionId,
      );
      if (!result.ok || (!result.attemptToken && !isAdmin)) {
        if (result.upgradeRequired) {
          void queryClient.invalidateQueries({
            queryKey: currentPlanQueryKey(user?.id),
          });
        }
        toast.error(
          result.message ||
            t("simulation.renewRequired", {
              defaultValue: SIMULATION_RENEWAL_BLOCK_MESSAGE,
            }),
          {
            action: result.upgradeRequired
              ? {
                  label: t("simulation.viewPlans", { defaultValue: "View plans" }),
                  onClick: () => navigate(`${ROUTES.SETTINGS}#plan`),
                }
              : undefined,
          },
        );
        return false;
      }
      applyResumeFromDraft(draft);
      setAttemptToken(result.attemptToken ?? null);
      return true;
    },
    [
      applyResumeFromDraft,
      isAdmin,
      navigate,
      queryClient,
      setAttemptToken,
      t,
      user?.id,
    ],
  );

  /**
   * Draft recovery detection.
   * Runs once after the user is known. If a non-expired draft exists for this
   * user, offer it via the Resume dialog. Separately, if the persisted
   * Zustand store restored us to a mid-flow step but we have no draft to
   * back that up, silently reset to avoid rendering a half-broken module.
   * Dashboard "Resume" passes `autoResumeFromDashboard` to apply immediately without the dialog.
   */
  useEffect(() => {
    if (recoveryHandled || !user?.id || isPlanLoading) {
      return;
    }
    if (!canStartSimulation(plan, isAdmin)) {
      setRecoveryHandled(true);
      return;
    }

    const navState = location.state as ExamSimulationLocationState | null;
    if (navState?.autoResumeFromDashboard) {
      const draft = getNewestRecoverableDraft(user.id);
      if (draft) {
        void authorizeAndResumeDraft(draft);
      }
      setRecoverableDraft(null);
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
      setRecoveryHandled(true);
      return;
    }

    const newest = getNewestRecoverableDraft(user.id);

    const midFlowSteps: ReadonlyArray<typeof currentStep> = [
      "instructions",
      "preparing",
      "module-intro",
      "module",
    ];
    const storeIsMidFlow = midFlowSteps.includes(currentStep);

    if (newest) {
      setRecoverableDraft(newest);
    } else if (storeIsMidFlow) {
      // Stale persisted store without a backing draft - clean slate.
      reset();
    }
    setRecoveryHandled(true);
  }, [
    recoveryHandled,
    user?.id,
    currentStep,
    reset,
    location.pathname,
    location.search,
    location.state,
    navigate,
    applyResumeFromDraft,
    authorizeAndResumeDraft,
    isAdmin,
    isPlanLoading,
    plan,
  ]);

  const handleResumeDraft = async () => {
    const draft = recoverableDraft;
    setRecoverableDraft(null);
    if (!draft) {
      return;
    }
    await authorizeAndResumeDraft(draft);
  };

  const handleDiscardDraft = () => {
    setRecoverableDraft(null);
    if (user?.id) {
      clearAllForUser(user.id);
    }
    reset();
  };

  // Reset state when the component unmounts (navigating away)
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const handleModuleConfirm = async () => {
    if (selection.modules.length === 0) {
      toast.error(t("simulation.selection.atLeastOneModule"));
      logger.event(
        "simulation.selection.validation_failed",
        {
          step: currentStep,
          reason: "no_modules_selected",
          examType: selection.examType,
          level: selection.level,
        },
        "warn",
        "Simulation module selection validation failed",
      );
      return;
    }

    // Sort selected modules based on the order defined in settings
    if (settings?.moduleOrder) {
      const sortedModules = [...selection.modules].sort((a, b) => {
        const indexA = settings.moduleOrder.indexOf(a);
        const indexB = settings.moduleOrder.indexOf(b);

        // If a module is not in the predefined order, move it to the end
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      });

      // Update the store with sorted modules
      setModules(sortedModules);
    }

    // Navigate to instructions screen
    logger.event(
      "simulation.selection.confirmed",
      {
        examType: selection.examType,
        level: selection.level,
        modules: selection.modules,
      },
      "info",
      "Simulation selection confirmed",
    );
    goToInstructions();
  };

  const handleStartSimulation = async () => {
    // Establish the session id first (one exam = one session, shared across all
    // modules). Reuse an existing session id (resume) so re-entering an exam is
    // not counted as a new mock.
    let activeSessionId = sessionId;
    if (!activeSessionId && user?.id) {
      generateSimulationSessionId(user.id);
      activeSessionId = useExamSimulationStore.getState().sessionId;
    }

    // Authorize the start against the user's plan (session quota + locked
    // level/provider). A new session consumes one mock; continuing an existing
    // one does not.
    if (selection.level && selection.examType) {
      setAttemptToken(null);
      const result = await startSimulationAttempt(
        selection.level,
        selection.examType,
        selection.modules,
        activeSessionId,
      );
      if (!result.ok) {
        if (result.upgradeRequired) {
          void queryClient.invalidateQueries({
            queryKey: currentPlanQueryKey(user?.id),
          });
          toast.error(
            result.message ||
              t("simulation.upgradeRequired", {
                defaultValue: "This is not included in your current plan.",
              }),
            {
              action: {
                label: t("simulation.viewPlans", {
                  defaultValue: "View plans",
                }),
                onClick: () => navigate(`${ROUTES.SETTINGS}#plan`),
              },
            },
          );
        } else {
          toast.error(
            result.message ||
              t("simulation.startFailed", {
                defaultValue:
                  "Could not start the simulation. Please try again.",
              }),
          );
        }
        return;
      }
      if (!result.attemptToken && !isAdmin) {
        toast.error(
          t("simulation.startFailed", {
            defaultValue: "Could not start the simulation. Please try again.",
          }),
        );
        return;
      }
      setAttemptToken(result.attemptToken ?? null);
    }

    logger.event(
      "simulation.started",
      {
        examType: selection.examType,
        level: selection.level,
        modules: selection.modules,
        userId: user?.id,
      },
      "info",
      "Simulation started",
    );
    // Navigate to preparing screen first
    goToPreparing();
  };

  const handlePreparingComplete = () => {
    // After preparing is complete, navigate to first module's introduction
    if (selection.modules.length > 0) {
      const firstModule = selection.modules[0];
      setCurrentModule(firstModule);
      setCurrentModuleIndex(0);
      logger.event(
        "simulation.preparing.completed",
        {
          sessionId,
          firstModule,
          modules: selection.modules,
        },
        "info",
        "Simulation preparing completed",
      );
      goToModuleIntroduction();
    }
  };

  const handleModuleIntroductionComplete = () => {
    // Navigate from introduction to module interface
    logger.event(
      "simulation.module.introduction.completed",
      {
        sessionId,
        module: currentModule,
        moduleIndex: currentModuleIndex,
      },
      "info",
      "Simulation module introduction completed",
    );
    goToModule();
  };

  const handleModuleFinish = () => {
    // Check if there are more modules
    if (currentModuleIndex < selection.modules.length - 1) {
      // Move to next module
      const nextIndex = currentModuleIndex + 1;
      const nextModule = selection.modules[nextIndex];
      logger.event(
        "simulation.module.completed",
        {
          sessionId,
          module: currentModule,
          moduleIndex: currentModuleIndex,
          nextModule,
        },
        "info",
        "Simulation module completed",
      );
      setCurrentModule(nextModule);
      setCurrentModuleIndex(nextIndex);
      // Show introduction for next module
      goToModuleIntroduction();
    } else {
      // All modules completed
      logger.event(
        "simulation.completed",
        {
          sessionId,
          modules: selection.modules,
        },
        "info",
        "Simulation completed",
      );
      setCurrentModule(null);
      setCurrentModuleIndex(0);
      // Prefetch fresh quota so Dashboard shows updated counts immediately
      // rather than relying on the passive background-refetch on PlanBanner mount.
      queryClient.prefetchQuery({
        queryKey: currentPlanQueryKey(user?.id),
        queryFn: getCurrentPlan,
      });
      queryClient.prefetchQuery({
        queryKey: diagnosticProfileQueryKey(user?.id),
        queryFn: getDiagnosticProfile,
      });
      goToFinished();
    }
  };

  // Render functions for each step - extracted to reduce cognitive complexity
  const renderExamTypeStep = () => <ExamTypeSelector onSelect={setExamType} />;

  const renderLevelStep = () => (
    <LevelSelector
      onSelect={setLevel}
      selectedLevel={selection.level}
      examType={selection.examType}
    />
  );

  const renderModulesStep = () => (
    <ModuleSelector
      selectedModules={selection.modules}
      onToggle={toggleModule}
      onConfirm={handleModuleConfirm}
      microphonePermission={microphonePermission}
      onRequestMicrophonePermission={requestMicrophonePermission}
      isMicrophoneSupported={isMicrophoneSupported}
    />
  );

  const renderInstructionsStep = () => {
    if (!selection.examType || !selection.level || !settings) {
      return null;
    }
    return (
      <InstructionsScreen
        examType={selection.examType}
        level={selection.level}
        selectedModules={selection.modules}
        settings={settings}
        microphonePermission={microphonePermission}
        onBack={goBack}
        onStartSimulation={handleStartSimulation}
        isLoading={isLoading}
      />
    );
  };

  const renderPreparingStep = () => {
    if (!selection.examType || !selection.level || !settings) {
      return null;
    }
    return (
      <PreparingScreen
        level={selection.level}
        examType={selection.examType}
        modules={selection.modules}
        settings={settings}
        userId={user?.id}
        onComplete={handlePreparingComplete}
        onError={(error) => {
          logger.error("Preparing screen error", error);
          toast.error(t("simulation.errors.unableToLoad"));
        }}
      />
    );
  };

  const renderModuleIntroStep = () => {
    if (!currentModule || !selection.examType || !selection.level) {
      return null;
    }

    // Use custom introduction for speaking module
    if (currentModule === "speaking") {
      if (isLoadingIntroduction && !moduleIntroduction) {
        return (
          <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
            <Card className="p-12 text-center max-w-lg w-full shadow-2xl border-t-4 border-t-primary animate-in fade-in duration-500">
              <div className="flex flex-col items-center gap-6">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                <div className="space-y-2">
                  <h3 className="font-ui text-xl font-bold">
                    {t("simulation.instructions.loadingQuestions")}
                  </h3>
                  <p className="font-ui text-muted-foreground">
                    {t("simulation.loadingNotice")}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        );
      }

      return (
        <SpeakingModuleIntroduction
          onStart={handleModuleIntroductionComplete}
          introduction={moduleIntroduction}
          defaultDuration={moduleIntroduction?.duration ?? undefined}
        />
      );
    }

    // Use standard ModuleIntroduction for other modules
    if (isLoadingIntroduction && !moduleIntroduction) {
      return (
        <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
          <Card className="p-12 text-center max-w-lg w-full shadow-2xl border-t-4 border-t-primary animate-in fade-in duration-500">
            <div className="flex flex-col items-center gap-6">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <div className="space-y-2">
                <h3 className="font-ui text-xl font-bold">
                  {t("simulation.instructions.loadingQuestions")}
                </h3>
                <p className="font-ui text-muted-foreground">
                  {t("simulation.loadingNotice")}
                </p>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return (
      <ModuleIntroduction
        module={currentModule}
        introduction={moduleIntroduction}
        onStart={handleModuleIntroductionComplete}
        defaultDuration={moduleIntroduction?.duration ?? undefined}
      />
    );
  };

  const renderModuleInterface = () => {
    if (!currentModule || !selection.examType || !selection.level) {
      return null;
    }

    const moduleProps = {
      examType: selection.examType,
      level: selection.level,
      settings,
      onModuleFinish: handleModuleFinish,
      sessionId,
    };

    switch (currentModule) {
      case "reading":
        return <ReadingInterface {...moduleProps} />;
      case "listening":
        return <ListeningInterface {...moduleProps} />;
      case "speaking":
        return <SpeakingInterface {...moduleProps} />;
      case "writing":
        return <WritingInterface {...moduleProps} />;
      default:
        return null;
    }
  };

  const renderFinishedStep = () => (
    <Card className="p-12 text-center max-w-2xl mx-auto shadow-xl border-t-4 border-t-primary animate-in fade-in zoom-in duration-500">
      <div className="mb-6 flex justify-center">
        <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center text-primary">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
      </div>

      <h2 className="text-3xl font-bold tracking-tight mb-4">
        {t("simulation.completion.title")}
      </h2>
      <p className="text-muted-foreground text-lg mb-8">
        {t("simulation.completion.completed", {
          examType: selection.examType?.toUpperCase(),
          level: selection.level,
        })}{" "}
        {t("simulation.completion.toSeeResults")}
      </p>

      <div className="bg-muted/50 p-6 rounded-lg mb-8 text-left">
        <p className="text-sm font-medium mb-3">{t("common.next")}:</p>
        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-primary font-bold">•</span>
            <span>
              {t("simulation.completion.goToResults")}{" "}
              {t("simulation.completion.simulationResults")}
              <Button
                variant="link"
                className="p-0 h-auto font-bold underline text-primary"
                onClick={() => {
                  const completedSessionId = sessionId;
                  reset();
                  navigate(
                    completedSessionId
                      ? `${ROUTES.RESULTS}?session=${encodeURIComponent(completedSessionId)}`
                      : ROUTES.RESULTS,
                  );
                }}
              >
                {t("simulation.results.title")}
              </Button>
              .
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary font-bold">•</span>
            <span>{t("simulation.history.description")}</span>
          </li>
        </ul>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          className="w-full min-w-[160px] text-lg py-6"
          onClick={() => {
            reset();
            navigate(ROUTES.DASHBOARD);
          }}
        >
          {t("nav.dashboard")}
        </Button>
      </div>
    </Card>
  );

  const renderStep = () => {
    switch (currentStep) {
      case "exam-type":
        return renderExamTypeStep();
      case "level":
        return renderLevelStep();
      case "modules":
        return renderModulesStep();
      case "instructions":
        return renderInstructionsStep();
      case "preparing":
        return renderPreparingStep();
      case "module-intro":
        return renderModuleIntroStep();
      case "module":
        return renderModuleInterface();
      case "finished":
        return renderFinishedStep();
      default:
        return null;
    }
  };

  const canGoBack =
    currentStep !== "exam-type" &&
    currentStep !== "instructions" &&
    currentStep !== "preparing" &&
    currentStep !== "module-intro" &&
    currentStep !== "module" &&
    currentStep !== "finished";

  // Helper function to get progress bar class for middle step
  const getMiddleProgressClass = () => {
    if (currentStep === "level") return "bg-primary";
    if (currentStep === "modules") return "bg-primary/30";
    return "bg-muted";
  };

  // Full screen steps don't need container wrapper
  const isFullScreen =
    currentStep === "preparing" ||
    currentStep === "module-intro" ||
    currentStep === "module" ||
    currentStep === "finished";

  const resumeDialog = (
    <ResumeSimulationDialog
      open={!!recoverableDraft}
      draft={recoverableDraft}
      onResume={handleResumeDraft}
      onDiscard={handleDiscardDraft}
    />
  );

  // Full screen steps render without container
  if (isFullScreen) {
    return (
      <>
        {currentStep === "module" ? <SimulationDraftSaveIndicator /> : null}
        {renderStep()}
        {resumeDialog}
      </>
    );
  }

  return (
    <StandardPageShell
      title={t("simulation.title", { defaultValue: "Exam Simulation" })}
      headerRight={<LanguageSwitcher />}
      className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-8 lg:px-8"
    >
      <div>
        {/* Header with back button */}
        <div className="mb-5 sm:mb-8">
          {canGoBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={goBack}
              className="gap-2 mb-4"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("common.back")}
            </Button>
          )}

          {/* Progress indicator - only show for selection steps */}
          {currentStep !== "instructions" && (
            <div className="flex items-center gap-2 mt-4">
              <div
                className={`h-2 flex-1 rounded-full ${
                  currentStep === "exam-type" ? "bg-primary" : "bg-primary/30"
                }`}
              />
              <div
                className={`h-2 flex-1 rounded-full ${getMiddleProgressClass()}`}
              />
              <div
                className={`h-2 flex-1 rounded-full ${
                  currentStep === "modules" ? "bg-primary" : "bg-muted"
                }`}
              />
            </div>
          )}
        </div>

        {/* Loading state */}
        {isLoading &&
          (currentStep === "modules" || currentStep === "instructions") && (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">
                Loading simulation settings...
              </p>
            </Card>
          )}

        {/* Error state */}
        {error &&
          (currentStep === "modules" || currentStep === "instructions") && (
            <Card className="p-8 text-center border-destructive">
              <p className="text-destructive">
                Failed to load simulation settings. Please go back and try
                again.
              </p>
            </Card>
          )}

        {/* Main content */}
        {!isLoading && !error && <div>{renderStep()}</div>}
      </div>
      {resumeDialog}
    </StandardPageShell>
  );
}
